/**
 * The attendant's shift.
 *
 * Reached behind a staff PIN, which authorises the *action* and is not a
 * session — the person standing at a self-service kiosk is a customer.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, major } from '@mgc/domain'
import { Button, Card, Notice, Spinner } from '@/components/ui'
import { refusalOf, setStaffToken, trpc } from '@/api/client'
import { useT } from '@/i18n'
import { useSession } from '@/store/session'

export function Shift({ onClose }: { onClose: () => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const authorise = useSession((s) => s.authorise)
  const deauthorise = useSession((s) => s.deauthorise)
  const staffId = useSession((s) => s.staffId)

  const [pin, setPin] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [float, setFloat] = useState('50000')
  const [counted, setCounted] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const staff = useQuery({ queryKey: ['staff'], queryFn: () => trpc.shifts.staff.query() })
  const current = useQuery({ queryKey: ['shift'], queryFn: () => trpc.shifts.current.query() })
  const history = useQuery({
    queryKey: ['shift-history'],
    queryFn: () => trpc.shifts.history.query({ limit: 7 }),
    enabled: staffId !== null
  })

  const verify = useMutation({
    mutationFn: async () => {
      if (selected === null) throw new Error('no staff selected')
      return trpc.branch.authoriseStaff.mutate({ staffId: selected, pin })
    },
    onSuccess: (result) => {
      // A refused PIN is a refusal from the server now, so it lands in
      // `onError` — there is no `{ ok: false }` shape to inspect here.
      authorise(result.staffId, result.name)
      setStaffToken(result.token)
      setPin('')
      setProblem(null)
    },
    onError: (error) => setProblem(refusalOf(error) === 'bad-pin' ? t('shift.badPin') : t('common.error'))
  })

  /**
   * Hand the kiosk back to the customer.
   *
   * There was no way to do this. `x-staff-id`, once set, was attached to every
   * request for the life of the page — including a customer's `orders.create` —
   * and only a reload cleared it. `deauthorise` existed in the store with zero
   * callers.
   */
  const release = useMutation({
    mutationFn: () => trpc.branch.releaseStaff.mutate(),
    onSettled: () => {
      setStaffToken(null)
      deauthorise()
      onClose()
    }
  })

  const start = useMutation({
    // No `staffId` and no `pin`. The server takes both from the authorisation
    // it minted, which also fixes a live bug: `verify.onSuccess` clears `pin`
    // before this card renders, so the value sent here was always ''.
    mutationFn: () => trpc.shifts.start.mutate({ openingFloat: major(Number(float || 0)) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['shift'] }),
    onError: (error) => setProblem(refusalOf(error) ?? t('common.error'))
  })

  const end = useMutation({
    mutationFn: () => trpc.shifts.end.mutate({ shiftId: current.data!.id, countedCash: major(Number(counted || 0)) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['shift'] }),
    onError: (error) => setProblem(refusalOf(error) ?? t('common.error'))
  })

  if (staff.isLoading || current.isLoading) return <Spinner />

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col gap-5 overflow-y-auto p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-[length:var(--text-kiosk-xl)] font-bold">{t('shift.title')}</h1>
        <span className="flex gap-2">
          {/*
            Closing the screen is not the same as giving up the authorisation,
            and only one of them used to exist. A kiosk left on the menu with a
            staff token still attached is one where a customer's order carries
            an attendant's authority.
          */}
          {staffId !== null && (
            <Button variant="ghost" onClick={() => release.mutate()} disabled={release.isPending}>
              {t('shift.lock')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t('settings.close')}
          </Button>
        </span>
      </header>

      {problem !== null && <Notice tone="bad">{problem}</Notice>}

      {staffId === null ? (
        <Card className="space-y-4 p-6">
          <div className="flex flex-wrap gap-3">
            {(staff.data ?? []).map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => setSelected(person.id)}
                aria-pressed={selected === person.id}
                className={`tap rounded-[var(--radius-pill)] border-2 px-5 py-3 font-medium ${
                  selected === person.id
                    ? 'border-[var(--color-brand-strong)] bg-[var(--color-brand-soft)]'
                    : 'border-[var(--color-line)]'
                }`}
              >
                {person.shortName ?? person.firstName}
              </button>
            ))}
          </div>

          <label className="block space-y-2">
            <span className="font-semibold">{t('shift.pin')}</span>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))}
              className="w-full rounded-[var(--radius-card)] border border-[var(--color-line)] px-4 py-3 text-[length:var(--text-kiosk-lg)] tracking-[0.4em]"
            />
          </label>

          <Button
            className="w-full"
            disabled={selected === null || pin.length < 4 || verify.isPending}
            onClick={() => verify.mutate()}
          >
            {t('common.confirm')}
          </Button>
        </Card>
      ) : current.data == null ? (
        <Card className="space-y-4 p-6">
          <p className="text-[var(--color-ink-soft)]">{t('shift.none')}</p>
          <label className="block space-y-2">
            <span className="font-semibold">{t('shift.openingFloat')}</span>
            <input
              inputMode="numeric"
              value={float}
              onChange={(event) => setFloat(event.target.value.replace(/\D/g, ''))}
              className="w-full rounded-[var(--radius-card)] border border-[var(--color-line)] px-4 py-3 tabular-nums"
            />
          </label>
          <Button className="w-full" disabled={start.isPending} onClick={() => start.mutate()}>
            {t('shift.start')}
          </Button>
        </Card>
      ) : (
        <OpenShift
          shift={current.data}
          counted={counted}
          setCounted={setCounted}
          onEnd={() => end.mutate()}
          ending={end.isPending}
        />
      )}

      {staffId !== null && (history.data ?? []).length > 0 && (
        <Card className="p-6">
          <h2 className="mb-3 font-bold">{t('shift.history')}</h2>
          <ul className="divide-y divide-[var(--color-line)]">
            {(history.data ?? [])
              .filter((row) => row.status === 'closed')
              .map((row) => (
                <li key={row.id} className="flex items-baseline justify-between gap-4 py-2">
                  <span className="flex-1">{new Date(row.startedAt).toLocaleDateString()}</span>
                  <span className="text-[var(--color-ink-soft)]">{row.staffName}</span>
                  <span className="tabular-nums">{format(row.summary.total)}</span>
                  {/*
                    Null is "nobody counted", which the schema keeps distinct
                    from a variance of zero all the way to here.
                  */}
                  <span
                    className={`w-28 text-right tabular-nums ${
                      row.variance === null
                        ? 'text-[var(--color-ink-soft)]'
                        : row.variance === 0n
                          ? ''
                          : 'text-[var(--color-bad)]'
                    }`}
                  >
                    {row.variance === null ? '—' : format(row.variance)}
                  </span>
                </li>
              ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

function OpenShift({
  shift,
  counted,
  setCounted,
  onEnd,
  ending
}: {
  shift: NonNullable<Awaited<ReturnType<typeof trpc.shifts.current.query>>>
  counted: string
  setCounted: (value: string) => void
  onEnd: () => void
  ending: boolean
}) {
  const t = useT()

  /**
   * Does the cached summary agree with the payments behind it?
   *
   * The columns on this screen are a cache; `order_payment` is the truth. A
   * drift is a completed order whose summary write was lost, and it is
   * invisible from here precisely because this screen reads the cache — which
   * is why the question has to be asked explicitly.
   */
  const drift = useQuery({
    queryKey: ['shift-reconcile', shift.id],
    queryFn: () => trpc.shifts.reconcile.query({ shiftId: shift.id }),
    refetchInterval: 60_000
  })

  return (
    <Card className="space-y-3 p-6">
      <Line label={t('payment.cash')} value={format(shift.summary.cash)} />
      <Line label={t('payment.card')} value={format(shift.summary.card)} />
      <Line label={t('payment.qpay')} value={format(shift.summary.qpay)} />
      <Line label={t('cart.total')} value={format(shift.summary.total)} strong />
      <Line label={t('shift.expected')} value={format(shift.expectedCash)} />

      {drift.data !== undefined && (
        <p className={drift.data.drift.length === 0 ? 'text-[var(--color-good)]' : 'text-[var(--color-bad)]'}>
          {drift.data.drift.length === 0
            ? t('shift.driftOk')
            : `${t('shift.drift')}: ${drift.data.drift.map((d) => d.field).join(', ')}`}
        </p>
      )}

      <label className="block space-y-2 pt-3">
        <span className="font-semibold">{t('shift.countedCash')}</span>
        <input
          inputMode="numeric"
          value={counted}
          onChange={(event) => setCounted(event.target.value.replace(/\D/g, ''))}
          className="w-full rounded-[var(--radius-card)] border border-[var(--color-line)] px-4 py-3 tabular-nums"
        />
      </label>

      {counted !== '' && (
        <Line
          label={t('shift.variance')}
          value={format((BigInt(counted) * 100n - shift.expectedCash) as never)}
          strong
        />
      )}

      {/* Refuses without a count: "nobody counted" is not "it balanced". */}
      <Button className="w-full" disabled={counted === '' || ending} onClick={onEnd}>
        {t('shift.end')}
      </Button>
    </Card>
  )
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <p
      className={`flex justify-between border-b border-[var(--color-line)] py-2 last:border-0 ${strong === true ? 'font-bold' : ''}`}
    >
      <span className={strong === true ? '' : 'text-[var(--color-ink-soft)]'}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </p>
  )
}
